/**
 * Admin — Push Notification Subscribers.
 *
 * Shows HR who has installed the PWA / enabled push, so they can
 * follow up personally with employees who haven't and understand
 * notification-delivery reliability.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bell, BellSlash, DeviceMobile, MagnifyingGlass } from '@phosphor-icons/react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { fmtKsa } from '@/utils/formatKsa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

export default function AdminPushSubscribers() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], subscribed_count: 0, active_employees_count: 0, hidden_terminated_count: 0 });
  const [q, setQ] = useState('');
  const [includeTerminated, setIncludeTerminated] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(
          `${API}/push/admin/subscribers`,
          { params: { include_terminated: includeTerminated ? 'true' : 'false' }, withCredentials: true },
        );
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [includeTerminated]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.rows;
    return data.rows.filter((r) =>
      (r.name || '').toLowerCase().includes(term)
      || (r.email || '').toLowerCase().includes(term)
      || (r.department || '').toLowerCase().includes(term)
    );
  }, [data.rows, q]);

  const percent = data.active_employees_count
    ? Math.round((data.subscribed_count / data.active_employees_count) * 100)
    : 0;

  return (
    <div dir={dir} className="min-h-screen bg-[#F8F7FB]">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-3">
            <Bell size={28} weight="duotone" />
            {isAr ? 'مشتركو الإشعارات' : 'Push Notification Subscribers'}
          </h1>
          <p className="text-sm text-[#655dc6]/70 mt-2">
            {isAr
              ? 'الموظفون الذين فعّلوا إشعارات التطبيق على أجهزتهم'
              : 'Employees who have enabled push notifications on their devices'}
          </p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-[#C4BFF0]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <Bell size={22} weight="duotone" />
                </div>
                <div>
                  <p className="text-xs text-[#655dc6]/70">
                    {isAr ? 'مفعّلون' : 'Subscribed'}
                  </p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="stat-subscribed">
                    {data.subscribed_count}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#C4BFF0]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-50 text-slate-500 flex items-center justify-center">
                  <BellSlash size={22} weight="duotone" />
                </div>
                <div>
                  <p className="text-xs text-[#655dc6]/70">
                    {isAr ? 'إجمالي الموظفين' : 'Total Employees'}
                  </p>
                  <p className="text-2xl font-bold text-slate-700" data-testid="stat-total">
                    {data.active_employees_count}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#C4BFF0]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#EDE9FE] text-[#655dc6] flex items-center justify-center">
                  <DeviceMobile size={22} weight="duotone" />
                </div>
                <div>
                  <p className="text-xs text-[#655dc6]/70">
                    {isAr ? 'نسبة التغطية' : 'Coverage'}
                  </p>
                  <p className="text-2xl font-bold text-[#655dc6]" data-testid="stat-percent">
                    {percent}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search + terminated toggle */}
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isAr ? 'ابحث بالاسم أو البريد أو القسم' : 'Search by name, email or department'}
              className="ps-9"
              data-testid="search-input"
            />
            <MagnifyingGlass size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#655dc6]/60" />
          </div>
          <label
            className={`flex items-center gap-2 text-xs rounded-full px-3 py-1.5 border cursor-pointer select-none whitespace-nowrap ${
              includeTerminated
                ? 'bg-red-50 border-red-300 text-red-700'
                : 'bg-white border-[#C4BFF0] text-[#655dc6] hover:bg-[#F5F3FF]'
            }`}
            data-testid="toggle-include-terminated"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={includeTerminated}
              onChange={(e) => setIncludeTerminated(e.target.checked)}
            />
            {isAr
              ? `إظهار المنتهية خدماتهم${data.hidden_terminated_count ? ` (${data.hidden_terminated_count})` : ''}`
              : `Show terminated${data.hidden_terminated_count ? ` (${data.hidden_terminated_count})` : ''}`}
          </label>
        </div>

        {/* Table */}
        <Card className="border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] text-base">
              {isAr ? `القائمة (${filtered.length})` : `List (${filtered.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-[#655dc6]/60 py-8">
                {isAr ? 'جاري التحميل…' : 'Loading…'}
              </p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10">
                <BellSlash size={40} weight="duotone" className="mx-auto text-[#655dc6]/40 mb-2" />
                <p className="text-sm text-[#655dc6]/70">
                  {isAr
                    ? 'لم يقم أي موظف بتفعيل الإشعارات بعد.'
                    : 'No employees have enabled notifications yet.'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-start">{isAr ? 'الاسم' : 'Name'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'البريد' : 'Email'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'القسم' : 'Department'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'الأجهزة' : 'Devices'}</TableHead>
                    <TableHead className="text-start">{isAr ? 'آخر تفعيل' : 'Last Enabled'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.user_id} data-testid={`row-${r.user_id}`}>
                      <TableCell className="font-medium text-[#655dc6]">
                        {r.name || '—'}
                        {r.is_terminated && (
                          <span className="ms-2 text-[10px] px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                            {isAr ? 'مُنهاة خدمته' : 'Terminated'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-[#655dc6]/80">{r.email}</TableCell>
                      <TableCell className="text-xs text-[#655dc6]/80">{r.department || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-semibold text-[#655dc6]" data-testid={`devices-${r.user_id}`}>
                            {r.device_count}
                          </span>
                          {(r.user_agents || []).map((ua, i) => (
                            <span key={i} className="text-[10px] text-[#655dc6]/60">
                              {ua}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-[#655dc6]/70">
                        {r.last_updated ? fmtKsa(r.last_updated) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
